import React, { useState, useEffect } from 'react';
import { withProps } from 'recompose';
import { PromiseState } from 'react-refetch';
import { useSelector } from 'react-redux';
import delve from 'dlv';
import { max } from 'd3-array';
import moment from 'moment';

import useMedia from 'hooks/useMedia';
import usePlaylistData from '../util/usePlaylistData';
import useTrendData from '../util/useTrendData';

import Table from 'components/data-visualization/Table/Table';
import Body from 'components/data-visualization/Table/Body';
import PlaylistTableRow from './PlaylistTableRow';
import TableLoader from '../../../utility/ContentLoader/TableLoader/TableLoader';
import { IPlaylistItem } from './PlaylistTableRow';
import { RootState } from 'redux/rootReducer';
import { ICountryFlagProps } from 'types/shared';

import DropdownTableHead from 'components/data-visualization/Table/DropdownHead';
import { getMenuItems, ITableHeader } from '../util/getMenuItems';
import PlaylistTableMobileRow from './PlaylistTableMobileRow';
import { TableCell, TableRow } from '../styles/PlaylistTableRow.style';
import { logToAmplitude } from 'helpers/logAmplitudeEvent';

export type IPosition = { periodKey: number; position: number };

interface IPlaylistTableProps {
  changeSortMetric: any;
  /** The entityData for each Entity (multiple in compare) from the API */
  entityData: {
    entity: any;
    playlist_count: number;
    playlists: any[];
  };
  /** Id of expanded playlist */
  expandedId: number;
  expandedCountryId: string;
  getPositionsTrend: (id: number) => void;
  /** Color of graph, the sames as selected customer's color or default one */
  graphColor: string;
  /** The headers of the Table in a key/value pair */
  headers: ITableHeader[];
  isLargeScreen: boolean;
  positionData: PromiseState<any>;
  setExpanded: (expanded: {
    id: number | null;
    countryId?: string | null;
  }) => any;
  setSortMetric: any;
  setSortOrder: any;
  /** The column which is the current sorting column */
  sortMetric: any;
  /** The current sorting order */
  sortOrder: string;
  trendData: PromiseState<any>;
  data: any;
  columns: ITableHeader[];
  priorityPlaylistsShown?: boolean;
  setPlaylistEditing: any;
  canAnnotate?: boolean;
}

const withChangeMetric = new Set([
  'displayName',
  'customerId',
  'stream_count_this_week_to_date',
  'stream_count_last_week_to_date',
  'stream_wow_in_progress',
  'skiprate_this_week_to_date',
  'track_count',
  'followers',
  'position',
  'entity_track_count',
]);

/** This displays the complete PlaylistTable including Table header */
const PlaylistTable: React.FC<IPlaylistTableProps> = ({
  changeSortMetric,
  entityData,
  expandedId,
  expandedCountryId,
  graphColor,
  headers,
  isLargeScreen,
  positionData,
  setExpanded,
  setSortMetric,
  setSortOrder,
  sortMetric,
  sortOrder,
  trendData,
  data,
  columns,
  priorityPlaylistsShown,
  setPlaylistEditing,
  canAnnotate,
}) => {
  const isSmallScreen = useMedia('max-width: 40em');
  const [selectedEntity, setSelectedEntity] = useState<any>();
  const [selectedTempEntity, setTempEntity] = useState<any>();
  const [period, setPeriod] = useState<null | number | Date>(null);
  const [tempPeriod, setTempPeriod] = useState<null | number | Date>(null);
  const [allPeriodKeys, setAllPeriodKeys] = useState<any>([]);

  const { fulfilled: positionsFulfilled, pending: positionsPending } =
    positionData;
  const { entityId, playlistData } = usePlaylistData(positionData);

  const { fulfilled: streamFulfilled, pending: streamPending } = trendData;
  const streamsData: any = useTrendData(trendData);

  const hasNoDataPositions =
    positionsFulfilled &&
    ((playlistData && playlistData.length === 0) || playlistData === null);
  const hasNoDataTrend =
    streamFulfilled &&
    ((streamsData && streamsData.length === 0) || streamsData === null);

  useEffect(() => {
    setPeriod(Number(max(allPeriodKeys)));
  }, [allPeriodKeys, setPeriod]);

  const getSelectedEntities = (
    dataPositions: IPosition[],
    dataStreams: any = [],
    timePeriod: any
  ): any[] => {
    const getItem = (data: any): any => data.periodKey === timePeriod;
    const selectedPosition = (dataPositions || []).find(getItem);
    const selectedStream = (dataStreams || []).find(getItem);
    return Object.assign({}, selectedPosition, selectedStream);
  };
  useEffect(() => {
    setSelectedEntity(getSelectedEntities(playlistData, streamsData, period));
  }, [period, playlistData, streamsData]);

  useEffect(() => {
    setTempEntity(getSelectedEntities(playlistData, streamsData, tempPeriod));
  }, [tempPeriod, playlistData, streamsData]);

  useEffect(() => {
    if (hasNoDataPositions && !playlistData && hasNoDataTrend && !streamsData)
      return;
    const totalData = []
      .concat(playlistData, streamsData)
      .filter((item: any) => !!item);
    if (totalData && totalData.length > 0) {
      setAllPeriodKeys(totalData.map((item: any) => item.periodKey));
    }
  }, [hasNoDataPositions, hasNoDataTrend, playlistData, streamsData]);

  const toggleExpand =
    (playlistId: number, playlistName: string, countryId?: string): any =>
    (): any => {
      setExpanded(
        playlistId === expandedId
          ? { id: null, countryId: null }
          : { id: playlistId, countryId }
      );
      if (playlistId !== expandedId) {
        logToAmplitude('Toggle Playlist', { playlistId, playlistName });
      }
    };
  const onTempPeriodChange = (nextPeriod: Date | number): void => {
    if (!nextPeriod) return;
    const closeDate = allPeriodKeys.find((date: Date | number) =>
      moment(nextPeriod).isSame(date, 'day')
    );
    if (closeDate) {
      setTempPeriod(closeDate);
    }
  };
  const onPeriodChange = (nextPeriod: Date | number): void => {
    if (!nextPeriod) return;
    const closeDate = allPeriodKeys.find((date: Date | number) =>
      moment(nextPeriod).isSame(date, 'day')
    );
    if (closeDate) {
      setPeriod(closeDate);
    }
  };

  const countries = useSelector((state: RootState) => state.countries.data);

  const playlists = entityData || data;
  const playlistsWithTerritories = playlists?.playlists.map((item: any) => {
    const playlistCountry = countries?.find(
      (original: ICountryFlagProps) => item?.countryId === original.id
    );
    return {
      ...item,
      ...(playlistCountry ? { playlistTerritory: playlistCountry } : {}),
    };
  });

  const playlistsData = {
    ...playlists,
    playlists: playlistsWithTerritories || [],
  };

  const graphProps = {
    allPeriodKeys,
    color: graphColor,
    entityId,
    expandedId,
    expandedCountryId,
    fulfilled: { position: positionsFulfilled, stream: streamFulfilled },
    hasNoData: { position: hasNoDataPositions, stream: hasNoDataTrend },
    isSmallScreen,
    onTempPeriodChange,
    onPeriodChange,
    pending: { position: positionsPending, stream: streamPending },
    playlistData,
    selectedEntity,
    selectedTempEntity,
    streamsData,
    toggleExpand,
  };

  return (
    <Table>
      {isLargeScreen && (
        <DropdownTableHead
          headers={headers}
          setSortMetric={setSortMetric}
          setSortOrder={setSortOrder}
          changeSortMetric={changeSortMetric}
          sortMetric={sortMetric}
          notSortable={new Set(['countryId'])}
          sortOrder={sortOrder}
          doubleLineMetric={true}
          hideVisually={null}
          withChangeMetric={withChangeMetric}
          predefinedMenuItems={getMenuItems(headers, true, true, true)}
          redesignedTable={true}
          isTopListPlaylist={!entityData}
          tableName="playlists"
        />
      )}

      <Body>
        {playlistsData?.playlists?.length ? (
          playlistsData.playlists.map(
            (playlist: IPlaylistItem, index: number) => {
              const canNotAnnotate =
                playlist?.playlistType === 'non_priority' && !canAnnotate;

              return isLargeScreen ? (
                <PlaylistTableRow
                  key={`playlist-table-row-${index}`}
                  playlist={playlist}
                  {...graphProps}
                  isTopList={!entityData}
                  columns={columns}
                  priorityPlaylistsShown={priorityPlaylistsShown}
                  setPlaylistEditing={setPlaylistEditing}
                  canNotAnnotate={canNotAnnotate}
                />
              ) : (
                <PlaylistTableMobileRow
                  key={`playlist-table-row-${index}`}
                  playlist={playlist}
                  {...graphProps}
                  isTopList={!entityData}
                  priorityPlaylistsShown={priorityPlaylistsShown}
                  setPlaylistEditing={setPlaylistEditing}
                  headers={headers}
                  canNotAnnotate={canNotAnnotate}
                />
              );
            }
          )
        ) : (
          <TableLoader
            columnCount={headers.length}
            rowComponent={TableRow}
            cellComponent={TableCell}
          />
        )}
      </Body>
    </Table>
  );
};

export default withProps(({ data }: any) => ({
  entityData: delve(data, 'entityData'),
  data,
}))(PlaylistTable);
